// src/components/common/PasswordStrengthMeter.jsx — Live Password Strength & Validation Checklist
import React, { useMemo } from "react";
import { Check, X } from "lucide-react";
import "./passwordStrengthMeter.css";

export function evaluatePassword(pwd, confirmPwd = null) {
  const password = pwd || "";
  const checks = {
    length: password.length >= 8,
    hasUpper: /[A-Z]/.test(password),
    hasLower: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSymbol: /[^A-Za-z0-9]/.test(password),
    matchesConfirm: confirmPwd !== null ? (password.length > 0 && password === confirmPwd) : true
  };

  const passedCount = [
    checks.length,
    checks.hasUpper,
    checks.hasLower,
    checks.hasNumber,
    checks.hasSymbol
  ].filter(Boolean).length;

  let score = 0; // 0 to 4
  let label = "Too short";
  let color = "#ff453a"; // Red

  if (password.length === 0) {
    score = 0;
    label = "";
    color = "transparent";
  } else if (passedCount <= 2) {
    score = 1;
    label = "Weak";
    color = "#ff453a"; // Red
  } else if (passedCount === 3) {
    score = 2;
    label = "Fair";
    color = "#ff9f0a"; // Orange
  } else if (passedCount === 4) {
    score = 3;
    label = "Good";
    color = "#30d158"; // Green
  } else {
    score = 4;
    label = "Strong";
    color = "#30d158"; // Emerald Green
  }

  const isValid = checks.length && checks.hasUpper && checks.hasLower && checks.hasNumber && checks.matchesConfirm;

  return { checks, score, label, color, isValid };
}

export default function PasswordStrengthMeter({ password, confirmPassword = null, showChecklist = true }) {
  const { checks, score, label, color } = useMemo(
    () => evaluatePassword(password, confirmPassword),
    [password, confirmPassword]
  );

  if (!password) return null;

  return (
    <div className="crm-pwd-meter-box">
      {/* 4-bar indicator */}
      <div className="crm-pwd-meter-header">
        <span className="crm-pwd-meter-title">Password strength:</span>
        <span className="crm-pwd-meter-badge" style={{ color }}>{label}</span>
      </div>

      <div className="crm-pwd-meter-bars">
        {[1, 2, 3, 4].map((step) => (
          <div
            key={step}
            className={`crm-pwd-meter-bar ${score >= step ? "active" : ""}`}
            style={{
              backgroundColor: score >= step ? color : undefined
            }}
          />
        ))}
      </div>

      {/* Criteria checklist */}
      {showChecklist && (
        <div className="crm-pwd-checklist">
          <div className={`crm-pwd-check-item ${checks.length ? "pass" : "fail"}`}>
            {checks.length ? <Check size={12} strokeWidth={2.5} /> : <X size={12} strokeWidth={2.5} />}
            <span>At least 8 characters</span>
          </div>

          <div className={`crm-pwd-check-item ${checks.hasUpper && checks.hasLower ? "pass" : "fail"}`}>
            {checks.hasUpper && checks.hasLower ? <Check size={12} strokeWidth={2.5} /> : <X size={12} strokeWidth={2.5} />}
            <span>Upper and lowercase letters</span>
          </div>

          <div className={`crm-pwd-check-item ${checks.hasNumber ? "pass" : "fail"}`}>
            {checks.hasNumber ? <Check size={12} strokeWidth={2.5} /> : <X size={12} strokeWidth={2.5} />}
            <span>At least one number</span>
          </div>

          <div className={`crm-pwd-check-item ${checks.hasSymbol ? "pass" : "fail"}`}>
            {checks.hasSymbol ? <Check size={12} strokeWidth={2.5} /> : <X size={12} strokeWidth={2.5} />}
            <span>At least one symbol (e.g. !@#$)</span>
          </div>

          {confirmPassword !== null && (
            <div className={`crm-pwd-check-item ${checks.matchesConfirm ? "pass" : "fail"}`}>
              {checks.matchesConfirm ? <Check size={12} strokeWidth={2.5} /> : <X size={12} strokeWidth={2.5} />}
              <span>Passwords match</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
